import React, { useState, useRef, useEffect } from 'react';
import { Responsive as ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { authHeaders } from '../utils/auth';

const DEFAULT_LAYOUT = [
    { i: 'config', x: 0, y: 0, w: 3, h: 4 },
    { i: 'tasks', x: 0, y: 4, w: 3, h: 6 },
    { i: 'logs', x: 3, y: 0, w: 9, h: 10 }
];

interface AgentTask {
    id: string;
    description: string;
    status: 'pending' | 'running' | 'done' | 'failed' | 'waiting_approval';
    result?: string;
    error?: string;
    created_at: string;
    updated_at?: string;
}

interface AgentSession {
    id: string;
    goal: string;
    tasks: AgentTask[];
    status: 'idle' | 'planning' | 'running' | 'paused' | 'done' | 'error';
    model: string;
    iterations: number;
    max_iterations: number;
}

const STATUS_ICONS: Record<string, string> = {
    pending: 'fa-clock text-slate-500',
    running: 'fa-spinner fa-spin text-blue-400',
    done: 'fa-check-circle text-green-400',
    failed: 'fa-times-circle text-red-400',
    waiting_approval: 'fa-hand-paper text-yellow-400',
};

const AgentModePanel: React.FC = () => {
    const [goal, setGoal] = useState('');
    const [session, setSession] = useState<AgentSession | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [humanInput, setHumanInput] = useState('');
    const [showHumanInput, setShowHumanInput] = useState(false);
    const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
    const [logs, setLogs] = useState<string[]>([]);
    const [model, setModel] = useState(localStorage.getItem('omni_model_agent') || 'qwen3-vl:4b');
    const [maxIterations, setMaxIterations] = useState(10);
    const logsEndRef = useRef<HTMLDivElement>(null);
    const pollRef = useRef<NodeJS.Timeout | null>(null);

    // Draggable Layout State
    const [isLocked, setIsLocked] = useState(true);
    const [layouts, setLayouts] = useState<any>({ lg: DEFAULT_LAYOUT });
    const [mounted, setMounted] = useState(false);
    
    // Custom width provider logic
    const [width, setWidth] = useState(1200);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMounted(true);
        const saved = localStorage.getItem('omnigate_agent_layout');
        if (saved) {
            try { const p = JSON.parse(saved);
                if (p) { Object.keys(p).forEach(k => { if (Array.isArray(p[k])) p[k].forEach((i:any) => delete i.static); }); }
                setLayouts(p); } catch { }
        }

        if (containerRef.current) {
            setWidth(containerRef.current.clientWidth);
        }

        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                if (entry.contentRect.width > 0) {
                    setWidth(entry.contentRect.width);
                }
            }
        });

        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    const onLayoutChange = (layout: any, allLayouts: any) => {
        setLayouts(allLayouts);
        localStorage.setItem('omnigate_agent_layout', JSON.stringify(allLayouts));
    };

    const addLog = (msg: string) => {
        const ts = new Date().toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setLogs(prev => [...prev.slice(-200), `[${ts}] ${msg}`]);
        setTimeout(() => (() => { const el = logsEndRef.current; if(el && el.parentElement) { el.parentElement.scrollTop = el.parentElement.scrollHeight; } })(), 50);
    };

    // Call backend /v1/agents endpoint
    const startAgent = async () => {
        if (!goal.trim()) return;
        setIsLoading(true);
        setLogs([]);
        setSession(null);
        addLog(`🚀 Uruchamiam agenta... Cel: "${goal.slice(0, 60)}${goal.length > 60 ? '...' : ''}"`);

        try {
            const res = await fetch('/v1/agents/configure', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    goal,
                    model,
                    max_iterations: maxIterations,
                    human_in_the_loop: true,
                })
            });

            if (res.ok) {
                const data = await res.json();
                addLog(`✅ Sesja agenta: ${data.session_id || data.id || 'unknown'}`);
                // Start polling for status
                startPolling(data.session_id || data.id);
                return;
            }
        } catch { }

        // Fallback: Local agent simulation with LLM planning
        addLog('ℹ️ Backend agent offline — tryb lokalny LLM planning');
        await runLocalAgentLoop(goal, model, maxIterations);
        setIsLoading(false);
    };

    // Local agent loop using LLM to generate and execute tasks
    const runLocalAgentLoop = async (agentGoal: string, agentModel: string, maxIter: number) => {
        const sessionId = `local-${Date.now()}`;

        // Step 1: LLM generates task decomposition
        addLog('🧠 LLM generuje plan zadań...');
        const planningPrompt = `You are an autonomous agent planning system.
Break down this goal into 3-7 specific, executable tasks.
Return ONLY a JSON array of task objects: [{"id":"t1","description":"..."},...]
Goal: ${agentGoal}`;

        let tasks: AgentTask[] = [];
        try {
            const apiKey = localStorage.getItem('omnigate_api_key') || '';
            const res = await fetch('/v1/chat/completions', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}`, 'X-API-Key': apiKey },
                body: JSON.stringify({
                    model: agentModel,
                    messages: [{ role: 'user', content: planningPrompt }],
                    max_tokens: 500,
                    stream: false,
                })
            });
            if (res.ok) {
                const data = await res.json();
                const content = data.choices?.[0]?.message?.content || '';
                const jsonMatch = content.match(/\[[\s\S]*\]/);
                if (jsonMatch) {
                    const parsed = JSON.parse(jsonMatch[0]);
                    tasks = parsed.map((t: any) => ({
                        id: t.id || `t${Math.random().toString(36).slice(2, 6)}`,
                        description: t.description,
                        status: 'pending' as const,
                        created_at: new Date().toISOString(),
                    }));
                }
            }
        } catch (e) {
            addLog(`⚠️ LLM plan failed: ${e}`);
        }

        if (tasks.length === 0) {
            // Fallback: generic tasks
            tasks = [
                { id: 't1', description: 'Analyze goal and gather context', status: 'pending', created_at: new Date().toISOString() },
                { id: 't2', description: 'Research and collect required information', status: 'pending', created_at: new Date().toISOString() },
                { id: 't3', description: 'Execute primary action', status: 'pending', created_at: new Date().toISOString() },
                { id: 't4', description: 'Verify results and finalize', status: 'pending', created_at: new Date().toISOString() },
            ];
        }

        addLog(`📋 Plan: ${tasks.length} zadań wygenerowanych`);

        const newSession: AgentSession = {
            id: sessionId,
            goal: agentGoal,
            tasks,
            status: 'running',
            model: agentModel,
            iterations: 0,
            max_iterations: maxIter,
        };
        setSession(newSession);

        // Step 2: Execute tasks one by one with human-in-the-loop
        for (let i = 0; i < tasks.length && i < maxIter; i++) {
            const task = tasks[i];
            addLog(`▶️ [${i + 1}/${tasks.length}] ${task.description}`);

            // Mark as waiting_approval
            task.status = 'waiting_approval';
            setSession(prev => prev ? { ...prev, tasks: [...prev.tasks.map(t => t.id === task.id ? task : t)] } : null);

            // Human approval
            const approved = await waitForHumanApproval(task);
            if (!approved) {
                task.status = 'failed';
                task.error = 'Odrzucone przez operatora';
                addLog(`⛔ Zadanie odrzucone: ${task.description}`);
                setSession(prev => prev ? { ...prev, tasks: [...prev.tasks.map(t => t.id === task.id ? task : t)], status: 'paused' } : null);
                continue;
            }

            // Execute task via LLM
            task.status = 'running';
            setSession(prev => prev ? { ...prev, tasks: [...prev.tasks.map(t => t.id === task.id ? task : t)], iterations: i + 1 } : null);

            try {
                const apiKey = localStorage.getItem('omnigate_api_key') || '';
                const execRes = await fetch('/v1/chat/completions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}`, 'X-API-Key': apiKey },
                    body: JSON.stringify({
                        model: agentModel,
                        messages: [
                            { role: 'system', content: `You are an autonomous agent. Execute the requested task concisely. Return results as plain text.` },
                            { role: 'user', content: `Overall goal: ${agentGoal}\n\nExecute this specific task: ${task.description}` }
                        ],
                        max_tokens: 400,
                        stream: false,
                    })
                });
                if (execRes.ok) {
                    const data = await execRes.json();
                    task.result = data.choices?.[0]?.message?.content || 'Task completed';
                    task.status = 'done';
                    addLog(`✅ Ukończono: ${task.description.slice(0, 50)}`);
                } else {
                    throw new Error(`HTTP ${execRes.status}`);
                }
            } catch (e: any) {
                task.status = 'failed';
                task.error = e.message;
                addLog(`❌ Błąd: ${e.message}`);
            }

            task.updated_at = new Date().toISOString();
            setSession(prev => prev ? { ...prev, tasks: [...prev.tasks.map(t => t.id === task.id ? task : t)] } : null);
        }

        const allDone = tasks.every(t => t.status === 'done' || t.status === 'failed');
        setSession(prev => prev ? { ...prev, status: allDone ? 'done' : 'paused' } : null);
        addLog(allDone ? '🎉 Sesja zakończona!' : '⏸️ Sesja zatrzymana');
        setIsLoading(false);
    };

    // Human-in-the-loop: wait for operator action
    const waitForHumanApproval = (task: AgentTask): Promise<boolean> => {
        setPendingTaskId(task.id);
        setShowHumanInput(true);
        setHumanInput('');
        addLog(`🤝 Czekam na zatwierdzenie: "${task.description.slice(0, 50)}..."`);

        return new Promise((resolve) => {
            const handler = (e: CustomEvent) => {
                window.removeEventListener('agent_approval' as any, handler);
                resolve(e.detail.approved);
            };
            window.addEventListener('agent_approval' as any, handler);
        });
    };

    const handleApprove = () => {
        setShowHumanInput(false);
        window.dispatchEvent(new CustomEvent('agent_approval', { detail: { approved: true, input: humanInput } }));
        addLog(`✅ Zatwierdzone${humanInput ? `: "${humanInput}"` : ''}`);
    };

    const handleReject = () => {
        setShowHumanInput(false);
        window.dispatchEvent(new CustomEvent('agent_approval', { detail: { approved: false } }));
        addLog('⛔ Odrzucone przez operatora');
    };

    const startPolling = (sessionId: string) => {
        pollRef.current = setInterval(async () => {
            try {
                const res = await fetch(`/v1/agents/${sessionId}/status`, { headers: authHeaders() });
                if (res.ok) {
                    const data = await res.json();
                    setSession(data);
                    if (data.status === 'done' || data.status === 'error') {
                        clearInterval(pollRef.current!);
                        setIsLoading(false);
                    }
                }
            } catch { }
        }, 2000);
    };

    useEffect(() => {
        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, []);

    const doneCount = session?.tasks.filter(t => t.status === 'done').length ?? 0;
    const totalCount = session?.tasks.length ?? 0;
    const progress = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

    if (!mounted) return null;

    return (
        <div ref={containerRef} className="flex flex-col h-full bg-[#131722] overflow-hidden min-h-[600px] relative pb-10">
            <style>{`
                .react-grid-layout { position: relative; transition: height 200ms ease; }
                .react-grid-item { transition: all 200ms ease; transition-property: left, top; }
                .react-grid-item.cssTransforms { transition-property: transform; }
                .react-grid-item.resizing { z-index: 1; will-change: width, height; }
                .react-grid-item.react-draggable-dragging { transition: none; z-index: 3; will-change: transform; }
                .react-grid-item.react-grid-placeholder { background: rgba(6, 182, 212, 0.2); border: 2px dashed rgba(6, 182, 212, 0.5); opacity: 0.5; z-index: 2; transition: none; }
                .drag-handle { cursor: grab; }
                .drag-handle:active { cursor: grabbing; }
                .panel-wrapper { height: 100%; display: flex; flex-direction: column; background: #1e222d; border: 1px solid #2a2e39; border-radius: 0.5rem; overflow: hidden; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5); }
                .panel-wrapper:hover { border-color: rgba(6, 182, 212, 0.3); }
            `}</style>

            <div className="absolute -top-10 right-4 z-50 flex gap-2">
                <button onClick={() => setIsLocked(!isLocked)} className={`text-[9px] px-2 py-1 rounded border transition-colors ${isLocked ? 'bg-red-900/40 text-red-400 border-red-800' : 'bg-slate-800 text-slate-400 border-slate-700 hover:text-white'}`}>
                    <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Locked' : 'Unlocked'}
                </button>
                <button onClick={() => { setLayouts({ lg: DEFAULT_LAYOUT }); localStorage.removeItem('omnigate_agent_layout'); }} className="text-[9px] bg-slate-800 text-slate-400 hover:text-white px-2 py-1 rounded border border-slate-700">
                    <i className="fas fa-sync-alt mr-1"></i> Reset Layout
                </button>
            </div>

            
                <div className="flex justify-between items-center sm:mb-4 mb-2 px-2 bg-[#131722]/80 border border-[#2a2e39] rounded py-2">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsLocked(!isLocked)} className={`text-[10px] px-3 py-1.5 rounded transition-all font-bold ${isLocked ? 'bg-red-500/20 text-red-400 border border-red-500/50 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : 'bg-green-500/20 text-green-400 border border-green-500/50'}`}>
                            <i className={`fas ${isLocked ? 'fa-lock' : 'fa-unlock'} mr-1`}></i> {isLocked ? 'Blokada Włączona' : 'Odblokowane (Przeciągaj)'}
                        </button>
                    </div>
                </div>
    <ResponsiveGridLayout
                className="layout flex-1"
                width={width}
                layouts={isLocked ? Object.fromEntries(Object.entries(layouts).map(([k, v]) => [k, v.map((i: any) => ({ ...i, static: true }))])) : layouts}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={50}
                onLayoutChange={onLayoutChange}
                // @ts-ignore
                isDraggable={!isLocked}
                // @ts-ignore
                isResizable={!isLocked}
                // @ts-ignore
                draggableHandle=".drag-handle"
                margin={[12, 12]}
            >
                {/* Panel 1: Agent Config */}
                <div key="config" className="panel-wrapper">
                    <div className={`px-3 py-2 border-b border-[#2a2e39] bg-[#131722]/60 flex justify-between items-center ${!isLocked ? 'drag-handle' : 'cursor-default'} relative`}>
                        <div className="flex items-center gap-2">
                            <i className="fas fa-robot text-cyan-400"></i>
                            <h2 className="text-[10px] font-black text-white uppercase tracking-widest">Agent Config</h2>
                        </div>
                        <i className="fas fa-grip-lines text-slate-600"></i>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        <div>
                            <label className="text-[10px] font-bold text-slate-500 uppercase block mb-1">Cel Agenta</label>
                            <textarea
                                rows={3}
                                value={goal}
                                onChange={e => setGoal(e.target.value)}
                                placeholder="Opisz cel agenta szczegółowo..."
                                className="w-full bg-[#131722] border border-[#2a2e39] text-slate-200 text-xs px-2 py-2 rounded focus:outline-none focus:border-cyan-500/50 resize-none font-mono"
                                disabled={isLoading}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Model</label>
                                <input
                                    value={model}
                                    onChange={e => setModel(e.target.value)}
                                    className="w-full bg-[#131722] border border-[#2a2e39] text-slate-300 text-[10px] px-2 py-1 rounded font-mono focus:outline-none focus:border-cyan-500/50"
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <label className="text-[9px] font-bold text-slate-500 uppercase block mb-1">Max Iter</label>
                                <input
                                    type="number"
                                    min={1} max={50}
                                    value={maxIterations}
                                    onChange={e => setMaxIterations(parseInt(e.target.value))}
                                    className="w-full bg-[#131722] border border-[#2a2e39] text-slate-300 text-[10px] px-2 py-1 rounded font-mono focus:outline-none focus:border-cyan-500/50"
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                        <button
                            onClick={startAgent}
                            disabled={isLoading || !goal.trim()}
                            className={`w-full py-2 rounded font-black text-[10px] uppercase tracking-widest transition-all ${isLoading || !goal.trim()
                                ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                : 'bg-cyan-600 hover:bg-cyan-500 text-white shadow-lg shadow-cyan-900/30'
                                }`}
                        >
                            <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-play'} mr-2`}></i>
                            {isLoading ? 'Agent Running...' : '▶ Launch Agent'}
                        </button>
                    </div>
                </div>

                {/* Panel 2: Agent Tasks & HITL */}
                <div key="tasks" className="panel-wrapper">
                    <div className={`px-3 py-2 border-b border-[#2a2e39] bg-[#131722]/60 flex justify-between items-center ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            <i className="fas fa-list-ol mr-1 text-blue-400"></i>Task List
                        </span>
                        {session && (
                            <span className="text-[9px] text-cyan-400 font-bold">{doneCount}/{totalCount}</span>
                        )}
                    </div>

                    {/* Progress Bar Header */}
                    {session && totalCount > 0 && (
                        <div className="px-4 py-2 border-b border-[#2a2e39] bg-[#131722]">
                            <div className="h-1.5 bg-[#2a2e39] rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="flex-1 overflow-y-auto p-2">
                        {/* Human-in-the-loop Approval inside tasks area */}
                        {showHumanInput && (
                            <div className="mb-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 space-y-2 animate-pulse-slow">
                                <p className="text-[10px] font-black text-yellow-400 uppercase tracking-wider flex items-center gap-1">
                                    <i className="fas fa-hand-paper"></i> Zatwierdzenie wymagane
                                </p>
                                <p className="text-[10px] text-slate-300">
                                    Zadanie: <span className="text-yellow-300">{session?.tasks.find(t => t.id === pendingTaskId)?.description}</span>
                                </p>
                                <input
                                    type="text"
                                    value={humanInput}
                                    onChange={e => setHumanInput(e.target.value)}
                                    placeholder="Opcjonalne wskazówki..."
                                    className="w-full bg-[#131722] border border-yellow-500/30 text-slate-200 text-[10px] px-2 py-1.5 rounded focus:outline-none font-mono"
                                />
                                <div className="flex gap-2">
                                    <button onClick={handleApprove} className="flex-1 py-1.5 bg-green-600 hover:bg-green-500 text-white text-[10px] font-black rounded uppercase tracking-widest">
                                        <i className="fas fa-check mr-1"></i>Zatwierdź
                                    </button>
                                    <button onClick={handleReject} className="flex-1 py-1.5 bg-red-600/50 hover:bg-red-600 text-red-300 text-[10px] font-black rounded uppercase tracking-widest">
                                        <i className="fas fa-times mr-1"></i>Odrzuć
                                    </button>
                                </div>
                            </div>
                        )}

                        {!session ? (
                            <div className="h-full flex items-center justify-center text-slate-600 text-[10px] p-4 text-center">Brak zaplanowanych zadań</div>
                        ) : (
                            <div className="space-y-1.5">
                                {session.tasks.map((task, i) => (
                                    <div key={task.id} className={`p-2.5 rounded border text-[10px] ${task.status === 'done' ? 'border-green-500/20 bg-green-500/5' :
                                        task.status === 'running' ? 'border-blue-500/30 bg-blue-500/10 animate-pulse' :
                                            task.status === 'failed' ? 'border-red-500/20 bg-red-500/5' :
                                                task.status === 'waiting_approval' ? 'border-yellow-500/30 bg-yellow-500/10' :
                                                    'border-[#2a2e39] bg-transparent'
                                        }`}>
                                        <div className="flex items-start gap-2">
                                            <i className={`fas ${STATUS_ICONS[task.status] || 'fa-circle text-slate-600'} mt-0.5 flex-shrink-0`}></i>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-slate-200 leading-tight">{i + 1}. {task.description}</p>
                                                {task.result && (
                                                    <p className="text-green-400 mt-1 text-[9px] opacity-80 truncate" title={task.result}>
                                                        → {task.result.slice(0, 80)}
                                                    </p>
                                                )}
                                                {task.error && (
                                                    <p className="text-red-400 mt-1 text-[9px]">✗ {task.error}</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Panel 3: Runtime Logs */}
                <div key="logs" className="panel-wrapper">
                    <div className={`px-3 py-2 border-b border-[#2a2e39] bg-[#131722]/60 flex justify-between items-center ${!isLocked ? 'drag-handle' : 'cursor-default'}`}>
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                            <i className="fas fa-terminal mr-1"></i>Agent Runtime Log
                        </span>
                        <div className="flex items-center gap-2">
                            {session && (
                                <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded ${session.status === 'done' ? 'text-green-400 bg-green-500/10' :
                                    session.status === 'running' ? 'text-blue-400 bg-blue-500/10' :
                                        session.status === 'paused' ? 'text-yellow-400 bg-yellow-500/10' :
                                            'text-slate-400 bg-slate-500/10'
                                    }`}>{session.status}</span>
                            )}
                            <button onClick={() => setLogs([])} className="text-[9px] text-slate-600 hover:text-slate-400">Clear</button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto p-3 font-mono text-[10px] space-y-0.5 bg-[#0a0c10] custom-scrollbar">
                        {logs.length === 0 ? (
                            <p className="text-slate-600 text-center py-8">Uruchom agenta aby widzieć logi...</p>
                        ) : (
                            logs.map((log, i) => (
                                <div key={i} className={`${log.includes('❌') || log.includes('⛔') ? 'text-red-400' :
                                    log.includes('✅') || log.includes('🎉') ? 'text-green-400' :
                                        log.includes('🤝') || log.includes('⚠️') ? 'text-yellow-400' :
                                            log.includes('▶️') ? 'text-blue-400' :
                                                log.includes('🚀') ? 'text-cyan-400' :
                                                    'text-slate-400'
                                    }`}>{log}</div>
                            ))
                        )}
                        <div ref={logsEndRef} />
                    </div>
                </div>
            </ResponsiveGridLayout>
        </div>
    );
};

export default AgentModePanel;
